/**
 * SPDX-FileCopyrightText: 2024 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { AbstractColumn } from '../columnClass.js'
import { useDataStore } from '../../../../../store/data.js'
import { FilterIds } from '../filter.js'
import { ColumnTypes } from '../columnHandler.js'

export default class RelationColumn extends AbstractColumn {

	constructor(col) {
		super(col)
		this.type = ColumnTypes.Relation
		this.subtype = ''
	}

	/**
	 * Format the value for display
	 * @param {unknown} value The value to format
	 * @return {string} The formatted value
	 */
	formatValue(value) {
		if (value === null || value === undefined) {
			return ''
		}
		// For single relations, return the value as is
		return String(value)
	}

	/**
	 * Parse the value from input
	 * @param {unknown} value The value to parse
	 * @return {unknown} The parsed value
	 */
	parseValue(value) {
		if (value === null || value === undefined || value === '') {
			return null
		}
		// For single relations, return the value as is
		return value
	}

	getValueString(valueObject) {
		valueObject = valueObject || this.value || null
		return this.getLabel(valueObject.value)
	}

	getLabel(rowId) {
		// Try to get relation data from the store
		try {
			const dataStore = useDataStore()

			const columnRelations = dataStore.getRelations(this.id)
			const option = columnRelations[rowId]

			return option ? option.label : ''
		} catch (error) {
			console.warn('Failed to get relation label:', error)
			return ''
		}
	}

	default() {
		return null
	}

	/**
	 * Check if filter matches the cell value
	 * @param {object} cell The cell to check
	 * @param {object} filter The filter to apply
	 * @return {boolean} Whether the filter matches
	 */
	isFilterFound(cell, filter) {
		const filterValue = (filter.magicValuesEnriched ? filter.magicValuesEnriched : filter.value).toLowerCase()
		const cellLabel = this.getLabel(cell.value)?.toLowerCase()
		const filterMethod = {
			[FilterIds.Contains]() { return cellLabel?.includes(filterValue) },
			[FilterIds.DoesNotContain]() { return !cellLabel?.includes(filterValue) },
			[FilterIds.IsEqual]() { return cellLabel === filterValue },
			[FilterIds.IsNotEqual]() { return cellLabel !== filterValue },
			[FilterIds.IsEmpty]() { return !cellLabel },
			[FilterIds.IsNotEmpty]() { return !!cellLabel },
		}[filter.operator.id]
		return super.isFilterFound(filterMethod, cell)
	}

	isSearchStringFound(cell, searchString) {
		const value = this.getValueString(cell)
		return super.isSearchStringFound(value, cell, searchString)
	}

}
